import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { ISingleIframeHeader } from "@app/dashboard/types/innerLayout/iframes/header";
import type { IAddIframePayload } from "@app/dashboard/types/innerLayout/iframes/add-iframe";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { THREE_SECONDS } from "@app/dashboard/constants/shared";
import { addIframe } from "@app/dashboard/services/inner_layout/iframes/add-iframe";
import { Button, SkeletonBase, Breadcrumbs } from "@unified-checkout/design-system";
import { Plus } from "@unified-checkout/design-system/icons/index";
import { useSettingsUtils } from "../../Settings/useSettingsUtils";
import { AddModal } from "../components/IframeModal/Modals/AddModal";
import styles from "./header.module.scss";

export const Header = ({
  isError,
  isLoading,
  count
}:
  ISingleIframeHeader
) => {
  const { t } = useTranslation();
  const { internalPagesBreadcrumbs } = useSettingsUtils();

  const [showAddModal, setShowAddModal] = useState<boolean>(false)
  const [iframeForm, setIframeForm] = useState({
    name: "",
    description: "",
    content_html: "",
    content_css: "",
    content_js: "",
  });

  const {
    mutate: addMutate,
    isPending: isAddPending,
    isError: isAddError,
    isSuccess: isAddSuccess,
    reset: resetAdd,
  } = useMutation({
    mutationKey: [QUERY_KEYS.ADD_IFRAME],
    mutationFn: (payload: IAddIframePayload) => addIframe(payload),

    onSuccess: () => {
      setTimeout(() => {
        setShowAddModal(false);
        clearIframeForm()
        resetAdd?.();
      }, THREE_SECONDS);
    },

    onError: () => {
      setTimeout(() => {
        setShowAddModal(false);
        clearIframeForm();
        resetAdd?.();
      }, THREE_SECONDS);
    },
  });

  const clearIframeForm = () => {
    setIframeForm({
      name: "",
      description: "",
      content_html: "",
      content_css: "",
      content_js: "",
    });
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    clearIframeForm()
    resetAdd?.();
  }

  const addModalProps = {
    open: showAddModal,
    isLoading: isAddPending,
    isError: isAddError,
    isSuccess: isAddSuccess,
    iframeForm: iframeForm,
    onClose: () => handleCloseAddModal(),
    onConfirm: () => addMutate(iframeForm),
    onFormChange: setIframeForm
  }

  return (
    !isError &&
    <div className={styles["header"]}>
      <div className={styles["header__breadcrumbs"]}>
        <Breadcrumbs
          linksProps={internalPagesBreadcrumbs.IFRAMES}
        />
      </div>

      {
        isLoading ?
          <SkeletonBase height="3.75rem" />
          :
          <div className={styles["header__main-info-box"]}>

            <div className={styles["header__main-info-box__info"]}>

              <div className={styles["header__main-info-box__info__amount-status-box"]}>
                <span>
                  {t('innerLayout.iFramesPage.header.title')}
                </span>
              </div>

              <div className={styles["header__main-info-box__info__date"]}>
                {`${t('innerLayout.iFramesPage.header.subTitle').replace('{{count}}', count)}`}
              </div>

            </div>

            <div className={styles["header__main-info-box__actions"]}>

              <Button
                size="md"
                color="primary"
                variant="contained"
                leadingIcon={Plus}
                text={t('innerLayout.iFramesPage.header.add')}
                onClickHandler={() => setShowAddModal(true)}
              />

            </div>

          </div>
      }
      <div className={styles["header__modal"]}>
        <AddModal {...addModalProps} />
      </div>
    </div>
  )
};